import { GRAYS, PALETTE } from "@eldrun/content";
import { ItemDrop, MatchState } from "@eldrun/protocol";
import { Color, Matrix4, Mesh, PerspectiveCamera, PlaneGeometry, Quaternion, Scene, Vector3, type BufferGeometry, type WebGLRenderer } from "three";
import { FrameTimer } from "../engine/frame-loop.ts";
import { FrameStats } from "../engine/frame-stats.ts";
import { StatsHud } from "../engine/stats-hud.ts";
import { debrisPieces, type DebrisSource } from "../game/debris.ts";
import { DISSOLVE, DissolvePool } from "../game/dissolves.ts";
import { DropLayer } from "../game/drop-layer.ts";
import { Shards } from "../game/shards.ts";
import { StructureLayer } from "../game/structure-layer.ts";
import { applyAtmosphere, createAtmosphereUniforms, sampleAtmosphere } from "../render/atmosphere.ts";
import type { ModelLibrary } from "../render/model-library.ts";
import { PostFx } from "../render/post/post-fx.ts";
import { countPrograms } from "../render/post/post-model.ts";
import { createFoliageMaterial } from "../render/shaders/foliage.ts";
import { withRole } from "../render/shaders/surface.ts";
import { gameColor } from "../render/style/roles.ts";
import { toonMaterial } from "../render/shaders/toon.ts";

export interface SceneryShowcase {
  stats(): Record<string, unknown>;
  stop(): void;
}

export interface SceneryShowcaseOptions {
  /** Fase do dia (0 a 1): de noite, os vagalumes; de dia, a poeira. */
  phase: number;
  /** Instante (s) da quebra, repetida a cada `CYCLE` s. */
  breakAt: number;
  /** "Reduzir lampejos": sem o estalo aceso, vagalumes sem piscar e o dissolve sem a borda acesa. */
  steady: boolean;
  /** Dissolve (F95) parado nesta fração (0 a 1) depois da quebra, para a captura; sem ele, anda no relógio. */
  dissolveAt?: number;
}

/** A cada quantos segundos os blocos voltam e quebram de novo. */
const CYCLE = 8;
/** Pedaços de uma quebra, como na partida (`BREAK_PIECES` da cena do jogo). */
const PIECES = 24;

/**
 * Vitrine dos efeitos de cenário (`?debug=entulho`, F90, sem servidor): uma rocha e uma árvore do
 * pacote e três peças de construção quebram no instante `breakAt` (e de novo a cada 8 s). O entulho sai
 * dos triângulos dos modelos, quica e assenta no chão; a camada ambiente roda em volta da câmera. Na
 * fileira da frente (F95), o que sai sem quebrar se desfaz no mesmo instante: uma bancada (o próprio
 * desmonte, com a borda na cor da classe), a Cruz (a borda neutra) e dois drops (o modelo do item e o cubo
 * de um item desconhecido, só em pontilhado).
 */
export function startSceneryShowcase(renderer: WebGLRenderer, library: ModelLibrary, options: SceneryShowcaseOptions): SceneryShowcase {
  const scene = new Scene();
  const atmosphere = createAtmosphereUniforms();
  const sample = sampleAtmosphere(options.phase);
  scene.background = sample.horizon;
  const camera = new PerspectiveCamera(55, 1, 0.1, 300);
  camera.position.set(0, 3.2, 8.5);
  camera.lookAt(0, 1, 0);
  // Chão no cinza médio do mundo (a malha neon só usa a luminância).
  const ground = new Mesh(new PlaneGeometry(80, 80), toonMaterial(atmosphere, `#${new Color().setScalar(GRAYS.medio).getHexString()}`, 0));
  ground.rotation.x = -Math.PI / 2;
  scene.add(ground);
  const foliage = createFoliageMaterial(atmosphere, { outline: true });
  const layer = new StructureLayer(scene, foliage, undefined, { library });
  const shards = new Shards(scene, () => 0);
  // Dissolve das famílias (F95): a fileira da frente sai sem quebrar.
  const dropMaterial = withRole(foliage, "interagivel");
  const dropLayer = new DropLayer(scene, dropMaterial, { library });
  const dropState = new MatchState();
  const dissolves = new DissolvePool(scene, atmosphere);
  dissolves.reduceFlashes = options.steady;
  const timer = new FrameTimer();
  const hud = new StatsHud(document.body);
  const resize = () => {
    renderer.setSize(window.innerWidth, window.innerHeight, false);
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
  };
  window.addEventListener("resize", resize);
  resize();

  // Os modelos do pacote (com a reserva vazia, sem nada para quebrar).
  const models: { mesh: Mesh; source: DebrisSource }[] = [];
  const model = (id: "rocha-a" | "carvalho-a", x: number, z: number, scale: number) => {
    const geometry = library.get(id, 0) as BufferGeometry | undefined;
    if (!geometry) return;
    const mesh = new Mesh(geometry, foliage);
    mesh.position.set(x, 0, z);
    mesh.scale.setScalar(scale);
    mesh.updateMatrix();
    scene.add(mesh);
    models.push({ mesh, source: { geometry, matrix: new Matrix4().compose(mesh.position, new Quaternion(), new Vector3(scale, scale, scale)) } });
  };
  model("rocha-a", -3.2, -1, 0.8);
  model("carvalho-a", 3.8, -4, 0.35);
  const pieces: [string, string, number][] = [
    ["1", "alicerce-pedra", -0.8],
    ["2", "parede-taipa", 1],
    ["3", "bancada", 2.4],
  ];
  // A bancada é o próprio desmonte (a borda na cor da classe, aqui a do Baluarte); a Cruz, a borda neutra.
  const vanishing: [string, string, number, string | undefined][] = [
    ["4", "bancada", -2.6, gameColor(PALETTE.classes.baluarte.hex)],
    ["5", "cruz", -0.9, undefined],
  ];
  const drops: [string, string, number][] = [
    ["1", "machado-pedra", 0.7],
    ["2", "item-que-nao-existe", 1.9],
  ];
  const place = () => {
    for (const [id, itemId, x] of pieces) layer.add(id, { itemId, x, y: 0, z: 0, yaw: 0 });
    for (const [id, itemId, x] of vanishing) layer.add(id, { itemId, x, y: 0, z: 2.2, yaw: 0.4 });
    for (const [key, itemId, x] of drops) {
      const drop = new ItemDrop();
      drop.itemId = itemId;
      drop.qty = 1;
      drop.x = x;
      drop.z = 2.4;
      dropState.drops.set(key, drop);
    }
    for (const m of models) m.mesh.visible = true;
  };
  /** A fileira da frente se desfaz (F95): sai do lote no mesmo quadro em que a cópia aparece. */
  let dissolveStart = 0;
  const vanish = (now: number) => {
    dissolveStart = now;
    for (const [id, , , edge] of vanishing) {
      const parts = layer.dissolveParts(id);
      dissolves.start(edge ? parts.map((p) => ({ ...p, edge })) : parts, now, edge !== undefined);
      layer.remove(id);
    }
    for (const [key] of drops) {
      const drop = dropState.drops.get(key);
      const part = drop && dropLayer.dissolvePart(key, drop);
      if (part) dissolves.start([part], now);
      dropState.drops.delete(key);
    }
  };
  const shatter = () => {
    const pop = (x: number, y: number) => (options.steady ? {} : { pop: { x, y, z: 0, size: 0.55 } });
    for (const [id, , x] of pieces) {
      const mesh = layer.objectOf(id) as Mesh | undefined;
      if (!mesh) continue;
      mesh.updateMatrix();
      shards.debris(debrisPieces({ geometry: mesh.geometry, matrix: mesh.matrix }, PIECES), { speed: 2.2, up: 2.4, life: 3.2, ...pop(x, 0.8) });
      layer.remove(id);
    }
    for (const m of models) {
      // Como na partida: o entulho até 2,5 m do pé (a árvore cai inteira; o entulho é do tronco).
      const low = debrisPieces(m.source, PIECES * 4).filter((p) => p.y < 2.5).slice(0, PIECES);
      shards.debris(low, { speed: 2.2, up: 2.4, life: 3.2, ...pop(m.mesh.position.x, 0.8) });
      m.mesh.visible = false;
    }
  };
  place();

  const counter = new FrameStats(renderer.info);
  const unwatch = counter.watch(scene);
  const post = new PostFx(renderer);
  post.bind(atmosphere);
  let last = performance.now();
  let clock = 0;
  let broken = false;
  renderer.setAnimationLoop((now: number) => {
    counter.begin();
    const dt = Math.min(0.1, (now - last) / 1000);
    timer.push(now - last);
    last = now;
    clock += dt;
    const phase = clock % CYCLE;
    if (!broken && phase >= options.breakAt) {
      shatter();
      vanish(now);
      broken = true;
    } else if (broken && phase < options.breakAt) {
      place();
      broken = false;
    }
    applyAtmosphere(atmosphere, sample, now / 1000);
    layer.update(now / 1000, camera.position.x, camera.position.z);
    dropLayer.update(dropState, now / 1000, camera.position.x, camera.position.z);
    // Parado na fração pedida (a captura), ou no relógio.
    dissolves.update(options.dissolveAt === undefined || !broken ? now : dissolveStart + Math.min(options.dissolveAt, 0.999) * DISSOLVE.ms);
    shards.ambient({ x: camera.position.x, z: camera.position.z, time: now / 1000, night: atmosphere.uNight.value, density: 1, steady: options.steady });
    shards.update(dt);
    post.render(scene, camera, dt, counter);
    const f = counter.end();
    const r = shards.rubbleStats;
    hud.update(now, () => [
      `Eldrun · efeitos de cenário · ${timer.fps.toFixed(0)} fps · draw calls ${f.drawCalls} (+${f.postCalls} do pós)`,
      `entulho ${r.live} (${r.resting} no chão) · ambiente ${shards.ambientCount} · dissolve ${dissolves.active}`,
    ]);
  });

  return {
    stats: () => ({
      fps: timer.fps,
      drawCalls: counter.last.drawCalls,
      programs: countPrograms(renderer.info.programs).scene,
      post: post.stats(counter.last),
      broken,
      rubble: shards.rubbleStats,
      ambient: shards.ambientCount,
      dissolves: dissolves.stats(),
      night: atmosphere.uNight.value,
    }),
    stop() {
      renderer.setAnimationLoop(null);
      post.dispose();
      unwatch();
      window.removeEventListener("resize", resize);
      layer.dispose();
      dropLayer.dispose();
      dissolves.dispose();
      hud.remove();
      dropMaterial.dispose();
      foliage.dispose();
      ground.geometry.dispose();
      (ground.material as { dispose(): void }).dispose();
    },
  };
}
